import { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getMissingRelationshipOwnerInfo, getPerson } from '../services/personService'
import { addLookupHistoryItemDescription, removeFromLookupHistory } from '../services/lookupHistoryService'
import { PERSON_ID_CONTEXT } from '../components/app/LookupContext'
import { setMostRecentlyViewed } from '../services/mostRecentlyViewedService'
import useNotifications from './useNotifications'

export default function usePerson(personId, forward = true) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [currentPerson, setCurrentPerson] = useState()
  const [fetchedMissingRelationshipOwnerInfo, setFetchedMissingRelationshipOwnerInfo] = useState(false)

  const fetchCurrentPerson = useCallback(() => {
    setCurrentPerson(null)
    setFetchedMissingRelationshipOwnerInfo(false)

    const setMetadata = (oldMetadata) => {
      const items = []
      const badges = []
      oldMetadata.lifeSpan &&
        items.push({ ...oldMetadata.lifeSpan, label: t('lifespan', 'Lifespan'), showLabel: false, field: 'lifeSpan' })
      oldMetadata.ids && items.push({ ...oldMetadata.ids, label: t('id', 'ID'), showLabel: false, field: 'ids' })
      oldMetadata.cisId &&
        items.push({ ...oldMetadata.cisId, label: t('cisId', 'CIS ID'), showLabel: true, field: 'cisId' })
      oldMetadata.contributorId &&
        items.push({
          ...oldMetadata.contributorId,
          label: t('contributorId', 'Contributor ID'),
          showLabel: true,
          field: 'contributorId',
        })
      oldMetadata.mrn && items.push({ ...oldMetadata.mrn, label: t('mrn', 'MRN'), showLabel: true, field: 'mrn' })
      oldMetadata.tombstonedBadge && badges.push({ ...oldMetadata.tombstonedBadge, field: 'tombstoned' })
      oldMetadata.ownerBadge &&
        badges.push({
          ...oldMetadata.ownerBadge,
          field: 'owner',
          private: oldMetadata.treeIsPrivate,
          tombstoned: oldMetadata.treeIsTombstoned,
        })
      oldMetadata.treeTypeBadge && badges.push({ ...oldMetadata.treeTypeBadge, field: 'treeType' })
      oldMetadata.accessControlBadge && badges.push({ ...oldMetadata.accessControlBadge, field: 'accessControl' })
      oldMetadata.forwardsToBadge && badges.push({ ...oldMetadata.forwardsToBadge, field: 'forwardsTo' })
      oldMetadata.forwardedFromBadge && badges.push({ ...oldMetadata.forwardedFromBadge, field: 'forwardedFromBadge' })

      return { header: oldMetadata.header, items, badges }
    }

    if (personId) {
      getPerson(personId, forward)
        .then((results) => {
          results.metadata = setMetadata(results.metadata)
          setCurrentPerson(results)
          addLookupHistoryItemDescription(PERSON_ID_CONTEXT, personId, results.name)
          setMostRecentlyViewed(PERSON_ID_CONTEXT, personId)
        })
        .catch((err) => {
          error(
            t('person.errors.gettingCurrentPerson', {
              defaultValue: `Error getting current person: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
          if (
            err &&
            err.response &&
            err.response.status &&
            (err.response.status >= 400 || err.response.status <= 499)
          ) {
            removeFromLookupHistory(PERSON_ID_CONTEXT, personId)
          }
        })
    }
  }, [error, forward, personId, t])

  const getPersonMissingRelationshipOwnerInfo = useCallback(async () => {
    if (currentPerson && !fetchedMissingRelationshipOwnerInfo) {
      try {
        const dmcPersonWithMissingRelationshipOwnerInfo = await getMissingRelationshipOwnerInfo(currentPerson)
        setFetchedMissingRelationshipOwnerInfo(true)
        setCurrentPerson({ ...dmcPersonWithMissingRelationshipOwnerInfo.data, metadata: currentPerson.metadata })
      } catch (err) {
        error(
          t('person.relationships.errors.gettingMissingRelationshipOwnerInfo', {
            defaultValue: `Error getting missing owner info for relationships: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      }
    }
  }, [currentPerson, error, fetchedMissingRelationshipOwnerInfo, t])

  useEffect(() => {
    fetchCurrentPerson(personId, forward)
  }, [fetchCurrentPerson, forward, personId])

  return { currentPerson, fetchCurrentPerson, getPersonMissingRelationshipOwnerInfo }
}
